import type { PropsWithChildren } from 'react';

import { type getFormatter, withExplicitSign } from '../../utilities';
import styles from './sankey-chart.module.css';
import type { SankeyNodeValidated } from './types';

/** Raw (unscaled) inflow/outflow totals for a node. */
export type SankeyNodeRaw = { in: number; out: number };

const formatSigned = (format: ReturnType<typeof getFormatter>) =>
  withExplicitSign(format);

export const SankeyNodeTooltipContent = ({
  node,
  format,
  raw,
}: {
  node: SankeyNodeValidated;
  format: ReturnType<typeof getFormatter>;
  raw: SankeyNodeRaw;
}) => {
  const label = node.label ?? node.id;

  // Role derived from link presence: only-out is a source, only-in a sink.
  const isPureSource = raw.out > 0 && raw.in === 0;
  const isPureSink = raw.in > 0 && raw.out === 0;

  if (isPureSource) {
    return (
      <Block label={label}>
        <dt>Outflow</dt>
        <dd>{format(raw.out)}</dd>
      </Block>
    );
  }

  if (isPureSink) {
    return (
      <Block label={label}>
        <dt>Inflow</dt>
        <dd>{format(raw.in)}</dd>
      </Block>
    );
  }

  const net = raw.in - raw.out;
  return (
    <Block label={label}>
      <dt>Inflow</dt>
      <dd>{format(raw.in)}</dd>
      <dt>Outflow</dt>
      <dd>{format(raw.out)}</dd>
      <dt>Net flow</dt>
      <dd>{formatSigned(format)(net)}</dd>
    </Block>
  );
};

const Block = ({ label, children }: PropsWithChildren<{ label: string }>) => (
  <div className={styles.tooltipBlock} data-testid="sankey-chart__node-tooltip">
    <div className={styles.tooltipTitle}>{label}</div>
    <dl className={styles.tooltipMetrics}>{children}</dl>
  </div>
);
